/**
 * GRAPHICS DEBUG PANEL — F7 toggles a lil-gui panel bound to `GFX`
 * (gfxConfig.ts). Every edit is applied live to the running match via
 * `GameRenderer.applyGfxConfig()`. "Save" persists the tune on this machine,
 * "Reset" returns to the shipped defaults, "Copy" puts the values on the
 * clipboard as JSON so a good tune can be pasted into GFX_DEFAULTS.
 *
 * lil-gui is dynamically imported on the first F7, so it costs nothing for
 * players who never open it. In a match the mouse is pointer-locked: press Esc
 * (pause) to get the cursor and drag the sliders.
 */
import { GFX, GFX_DEFAULTS, gfxQuality, gfxTarget, resetGfxTune, saveGfxTune } from "./gfxConfig.js";

type GUI = import("lil-gui").GUI;
let gui: GUI | null = null;
let opening = false;
let statusTimer = 0;

function apply(): void {
  gfxTarget()?.applyGfxConfig();
}

async function open(): Promise<void> {
  if (gui || opening) return;
  opening = true;
  try {
    const { GUI } = await import("lil-gui");
    const g = new GUI({ title: "Graphics (F7)", width: 300 });
    g.domElement.style.zIndex = "100000";
    const status = { info: "" };
    g.add(status, "info").name("status").disable().listen();
    g.add({ q: gfxQuality() }, "q").name("preset (Settings)").disable();

    const look = g.addFolder("Look");
    look.add(GFX, "exposure", 0.3, 2.5, 0.01).onChange(apply);
    look.add(GFX, "envIntensity", 0, 2, 0.01).name("env light").onChange(apply);
    look.add(GFX, "hdri").name("HDRI sky (High)").onChange(apply);
    look.add(GFX, "fogDensity", 0.25, 4, 0.05).name("fog density").onChange(apply);

    const bloom = g.addFolder("Bloom");
    bloom.add(GFX, "bloomStrengthNeon", 0, 2, 0.01).name("neon strength").onChange(apply);
    bloom.add(GFX, "bloomRadiusNeon", 0, 1.5, 0.01).name("neon radius").onChange(apply);
    bloom.add(GFX, "bloomThresholdNeon", 0, 1.5, 0.01).name("neon threshold").onChange(apply);
    bloom.add(GFX, "bloomStrengthDay", 0, 2, 0.01).name("day strength").onChange(apply);
    bloom.add(GFX, "bloomRadiusDay", 0, 1.5, 0.01).name("day radius").onChange(apply);
    bloom.add(GFX, "bloomThresholdDay", 0, 1.5, 0.01).name("day threshold").onChange(apply);

    const ao = g.addFolder("Ambient occlusion (High)");
    ao.add(GFX, "aoIntensity", 0, 8, 0.05).name("intensity").onChange(apply);
    ao.add(GFX, "aoRadius", 0.2, 6, 0.05).name("radius (m)").onChange(apply);
    ao.add(GFX, "aoDistanceFalloff", 0.1, 4, 0.05).name("falloff").onChange(apply);

    const sh = g.addFolder("Shadows");
    sh.add(GFX, "shadowBias", -0.003, 0.001, 0.00005).name("bias").onChange(apply);
    sh.add(GFX, "shadowNormalBias", 0, 0.2, 0.002).name("normal bias").onChange(apply);
    sh.close();

    g.add({ save: () => saveGfxTune() }, "save").name("Save (this machine)");
    g.add({ reset: () => { resetGfxTune(); g.controllersRecursive().forEach((c) => c.updateDisplay()); apply(); } }, "reset").name("Reset to defaults");
    g.add({ copy: () => {
      // only the values that differ from the shipped defaults
      const diff: Record<string, unknown> = {};
      for (const k of Object.keys(GFX_DEFAULTS) as (keyof typeof GFX)[]) if (GFX[k] !== GFX_DEFAULTS[k]) diff[k] = GFX[k];
      void navigator.clipboard?.writeText(JSON.stringify(diff, null, 2));
    } }, "copy").name("Copy changed values");

    const tick = (): void => { status.info = gfxTarget()?.gfxStatus() ?? "no match"; };
    tick();
    statusTimer = window.setInterval(tick, 500);
    gui = g;
  } catch (e) {
    console.warn("[gfx panel] failed to open", e);
  } finally {
    opening = false;
  }
}

function close(): void {
  window.clearInterval(statusTimer);
  gui?.destroy();
  gui = null;
}

let installed = false;
/** Install the F7 toggle once for the tab (main.ts). */
export function installGfxPanel(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("keydown", (e) => {
    if (e.code !== "F7") return;
    e.preventDefault();
    if (gui) close(); else void open();
  });
}
